import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaCalendarAlt, FaTrash, FaCheckCircle, FaClock, FaTimesCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './View.css';
import Header from '../../components/Header';
import { BASE_URL } from '../../config/apiConfig';
import Footer from '../../components/Footer';

const View = () => {
  const [appointments, setAppointments] = useState([]);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [completedAppointments, setCompletedAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [cancelledAppointments, setCancelledAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const navigate = useNavigate();

  // Get auth header for API requests
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    // Check if user is logged in as admin
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    
    if (!token || userRole !== 'ADMIN') {
      navigate('/login');
      return;
    }

    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/admin/appointments`, getAuthHeader());
        const allAppointments = response.data;
         
        // Filter appointments by status - check both status and appointmentStatus fields
        const completed = allAppointments.filter(app => 
          (app.appointmentStatus && app.appointmentStatus.toLowerCase() === 'completed') || 
          (app.status && app.status.toLowerCase() === 'completed') ||
          (app.appointmentStatus && app.appointmentStatus.toLowerCase() === 'reviewed') ||
          (app.status && app.status.toLowerCase() === 'reviewed')
        );
        
        const pending = allAppointments.filter(app => 
          (app.appointmentStatus && app.appointmentStatus.toLowerCase() === 'pending') ||
          (app.status && app.status.toLowerCase() === 'pending')
        );
        
        const upcoming = allAppointments.filter(app => 
          (app.appointmentStatus && app.appointmentStatus.toLowerCase() === 'upcoming') ||
          (app.status && app.status.toLowerCase() === 'upcoming')
        );
        
        const cancelled = allAppointments.filter(app => 
          (app.appointmentStatus && app.appointmentStatus.toLowerCase() === 'cancelled') ||
          (app.status && app.status.toLowerCase() === 'cancelled')
        );
        
        // Set all the filtered appointment arrays
        setAppointments(allAppointments);
        setPendingAppointments(pending);
        setCompletedAppointments(completed);
        setUpcomingAppointments(upcoming);
        setCancelledAppointments(cancelled);
        setTotalAppointments(allAppointments.length);
        
        
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError('Failed to fetch appointments. Please try again later.');
        setLoading(false);
        
        // If unauthorized, redirect to login
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.clear();
          navigate('/login');
        }
      }
    };

    fetchAppointments();
  }, [navigate]);

  


  // Update the loading state
  if (loading) {
    return (
      <>
        <Header />
        <div className="admin-view-loading">
          <div className="admin-view-spinner"></div>
          <p>Loading appointments data...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="admin-view-container">
          <div className="admin-view-error">
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="admin-view-container">
        <header className="admin-view-header">
          <h1 className="admin-view-title">Appointments Dashboard</h1>
          <div className="admin-view-summary-cards">
            <div className="admin-view-summary-card total">
              <div className="admin-view-summary-icon total">
                <FaCalendarAlt />
              </div>
              <div className="admin-view-summary-content">
                <div className="admin-view-summary-count">{totalAppointments}</div>
                <h2 className="admin-view-summary-title">Total Appointments</h2>
              </div>
            </div>
            
            <div className="admin-view-summary-card completed">
              <div className="admin-view-summary-icon completed">
                <FaCheckCircle />
              </div>
              <div className="admin-view-summary-content">
                <div className="admin-view-summary-count">{completedAppointments.length}</div>
                <h2 className="admin-view-summary-title">Completed</h2>
              </div>
            </div>
            
            <div className="admin-view-summary-card pending">
              <div className="admin-view-summary-icon pending">
                <FaClock />
              </div>
              <div className="admin-view-summary-content">
                <div className="admin-view-summary-count">{pendingAppointments.length}</div>
                <h2 className="admin-view-summary-title">Pending</h2>
              </div>
            </div>
            
            <div className="admin-view-summary-card upcoming">
              <div className="admin-view-summary-icon upcoming">
                <FaCalendarAlt />
              </div>
              <div className="admin-view-summary-content">
                <div className="admin-view-summary-count">{upcomingAppointments.length}</div>
                <h2 className="admin-view-summary-title">Upcoming</h2>
              </div>
            </div>
            
            <div className="admin-view-summary-card cancelled">
              <div className="admin-view-summary-icon cancelled">
                <FaTimesCircle />
              </div>
              <div className="admin-view-summary-content">
                <div className="admin-view-summary-count">{cancelledAppointments.length}</div>
                <h2 className="admin-view-summary-title">Cancelled</h2>
              </div>
            </div>
          </div>
        </header>
        
        <section className="admin-view-section">
          <h2 className="admin-view-section-title">Pending & Upcoming Appointments</h2>
          {pendingAppointments.length === 0 && upcomingAppointments.length === 0 ? (
            <p className="admin-view-no-data">No pending or upcoming appointments found.</p>
          ) : (
            <div className="admin-view-table-container">
              <table className="admin-view-table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Doctor</th>
                    <th>Specialization</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...pendingAppointments, ...upcomingAppointments].map(appointment => (
                    <tr key={appointment.appointmentId}>
                      <td data-label="Patient Name">{appointment.userName || 'Unknown'}</td>
                      <td data-label="Doctor">{appointment.doctorName || 'Unknown'}</td>
                      <td data-label="Specialization">{appointment.doctorSpecialization || 'N/A'}</td>
                      <td data-label="Date & Time"> <div>{new Date(appointment.appointmentDate).toLocaleDateString()}</div>
                      <span>{appointment.appointmentTime}</span></td>
                      <td data-label="Status">
                        <span className={`status-badge ${appointment.appointmentStatus?.toLowerCase() || appointment.status?.toLowerCase() || 'unknown'}`}>
                          {(appointment.appointmentStatus || appointment.status || 'Unknown').charAt(0).toUpperCase() + 
                           (appointment.appointmentStatus || appointment.status || 'Unknown').slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        
        <section className="admin-view-section">
          <h2 className="admin-view-section-title">Completed Appointments</h2>
          {completedAppointments.length === 0 ? (
            <p className="admin-view-no-data">No completed appointments found.</p>
          ) : (
            <div className="admin-view-table-container">
              <table className="admin-view-table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Doctor</th>
                    <th>Specialization</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {completedAppointments.map(appointment => (
                    <tr key={appointment.appointmentId || `completed-${Math.random()}`}>
                      <td data-label="Patient Name">{appointment.userName || 'Unknown'}</td>
                      <td data-label="Doctor">{appointment.doctorName || 'Unknown'}</td>
                      <td data-label="Specialization">{appointment.doctorSpecialization || 'N/A'}</td>
                      <td data-label="Date & Time"> <div>{new Date(appointment.appointmentDate).toLocaleDateString()}</div>
                      <span>{appointment.appointmentTime}</span></td>
                      <td data-label="Status">
                        <span 
                          className={`status-badge ${
                            appointment.appointmentStatus?.toLowerCase() === 'completed' || 
                            appointment.status?.toLowerCase() === 'completed' ? 'completed' : 
                            appointment.appointmentStatus?.toLowerCase() === 'reviewed' || 
                            appointment.status?.toLowerCase() === 'reviewed' ? 'reviewed' : 
                            'completed'
                          }`}
                        >
                          {appointment.appointmentStatus === 'COMPLETED' || appointment.status === 'COMPLETED' ? 'Completed' :
                           appointment.appointmentStatus === 'REVIEWED' || appointment.status === 'REVIEWED' ? 'Reviewed' :
                           'Completed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        
        <section className="admin-view-section">
          <h2 className="admin-view-section-title">Cancelled Appointments</h2>
          {cancelledAppointments.length === 0 ? (
            <p className="admin-view-no-data">No cancelled appointments found.</p>
          ) : (
            <div className="admin-view-table-container">
              <table className="admin-view-table">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Doctor</th>
                    <th>Specialization</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cancelledAppointments.map(appointment => (
                    <tr key={appointment.appointmentId}>
                      <td data-label="Patient Name">{appointment.userName || 'Unknown'}</td>
                      <td data-label="Doctor">{appointment.doctorName || 'Unknown'}</td>
                      <td data-label="Specialization">{appointment.doctorSpecialization || 'N/A'}</td>
                      <td data-label="Date & Time"> <div>{new Date(appointment.appointmentDate).toLocaleDateString()}</div>
                      <span>{appointment.appointmentTime}</span></td>
                      <td data-label="Status">
                        <span className={`status-badge ${appointment.appointmentStatus?.toLowerCase() || appointment.status?.toLowerCase() || 'unknown'}`}>
                          {(appointment.appointmentStatus || appointment.status || 'Unknown').charAt(0).toUpperCase() + 
                           (appointment.appointmentStatus || appointment.status || 'Unknown').slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      <Footer />
    </>
  );
};

export default View;
